import React from 'react';
import { TFunction } from 'i18next';
import * as Yup from 'yup';
import _get from 'lodash/get';
import {
  FieldChangeCallBackArgs,
  FieldItem,
  Values,
} from '@lib/interfaces/form';
import FieldTitleRender from '@lib/components/FieldTitleRender/FieldTitleRender';
import FieldNames from '@lib/enums/fieldNames/interventionFieldNames';
import {
  getOptionsArrayValidation,
  getOptionValidation,
} from '@lib/utils/validationUtils';
import Select, {
  SelectComponents,
  SelectProps,
} from '@lib/components/Select/Select';
import { GetLoadOptions } from '@lib/components/Select/types';
import ChipSingleValue from '@lib/components/Select/components/ChipSingleValue';
import MultiSelectValueContainer from '@lib/components/Select/components/MultiSelectValueContainer';
import formStyles from '@lib/assets/styles/Form.module.scss';

type Args = {
  t: TFunction<'translation', undefined>;
  isEditForm: boolean;
  getTasksLoadOptions: GetLoadOptions;
  // From useTaskAndCompletionsPicker.
  openTaskStep: () => void;
  openCompletionsStep: () => void;
};

// The Intervention form's "Task and completions" section — both fields are
// read-only selects that open the TaskAndCompletionsModal wizard.
export default function getTaskAndCompletionsFormFields({
  t,
  isEditForm,
  getTasksLoadOptions,
  openTaskStep,
  openCompletionsStep,
}: Args): FieldItem[] {
  return [
    {
      name: FieldNames.TaskAndCompletionsTitle,
      customRender: () => (
        <FieldTitleRender
          key="task-and-completions"
          title={t('task-and-completions')}
          variant="body"
          strong
          hasBottomSpace
        />
      ),
      hideErrorsSpace: true,
    },
    {
      name: FieldNames.Task,
      label: t('related-task'),
      element: Select,
      // Read-only in edit mode: a field the user can't change must not be
      // able to block the submit.
      validation: isEditForm ? undefined : getOptionValidation({ t }),
      componentProps: {
        isAsync: true,
        getLoadOptions: getTasksLoadOptions,
        defaultOptions: false,
        isClearable: true,
        isSearchable: false,
        openMenuOnClick: false,
        openMenuOnFocus: false,
        menuIsOpen: false,
        onSelectWrapperMouseDown: openTaskStep,
        components: {
          SingleValue: ChipSingleValue,
        } as unknown as SelectComponents,
        disabled: isEditForm,
        disabledTooltipProps: isEditForm
          ? undefined
          : {
              body: t('choose-site-first'),
            },
      } as SelectProps,
      isUseWatch: true,
      isDisabledFn: isEditForm
        ? undefined
        : (values: Values) => !_get(values, `${FieldNames.Site}.value`),
      formItemClassName: formStyles.formColumn,
      getFieldKeyFn: (values: Values, name?: string) => {
        const selectedSiteId = _get(values, `${FieldNames.Site}.value`);
        const selectedSiteAreaId = _get(values, `${FieldNames.SiteArea}.value`);
        const selectedAssetId = _get(values, `${FieldNames.Asset}.value`);
        return `${name}-site-${selectedSiteId}-area-${selectedSiteAreaId}-asset-${selectedAssetId}`;
      },
      fieldChangeCallBack: ({
        setValue,
        name,
        trigger,
      }: FieldChangeCallBackArgs) => {
        if (name === FieldNames.Task) {
          setValue(FieldNames.Completions, [], { shouldValidate: false });
          trigger(FieldNames.Completions);
        }
      },
    },
    {
      name: FieldNames.Completions,
      label: t('completions'),
      element: Select,
      // Required once a Task is picked — but only on create: in edit mode the
      // field is read-only, so requiring it would block every save.
      validation: isEditForm
        ? undefined
        : Yup.array().when(FieldNames.Task, (deps, schema) => {
            const taskValue = _get(deps, [0, 'value']);
            return taskValue
              ? getOptionsArrayValidation({ required: true, t })
              : schema.nullable();
          }),
      componentProps: {
        options: [],
        isMulti: true,
        isClearable: true,
        isSearchable: false,
        openMenuOnClick: false,
        openMenuOnFocus: false,
        menuIsOpen: false,
        onSelectWrapperMouseDown: openCompletionsStep,
        components: { ValueContainer: MultiSelectValueContainer },
        disabled: isEditForm,
        disabledTooltipProps: isEditForm
          ? undefined
          : {
              body: t('choose-task-first'),
            },
      } as SelectProps,
      isUseWatch: true,
      isDisabledFn: isEditForm
        ? undefined
        : (values: Values) => !_get(values, `${FieldNames.Task}.value`),
      formItemClassName: formStyles.formColumn,
      getFieldKeyFn: (values: Values, name?: string) => {
        const selectedTaskId = _get(values, `${FieldNames.Task}.value`);
        return `${name}-task-${selectedTaskId}`;
      },
      isOptionalFn: isEditForm
        ? undefined
        : (values: Values) => !_get(values, `${FieldNames.Task}.value`),
    },
  ];
}
